import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StorageContainer } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { formatBytes } from '../lib/format.js';
import { toast } from '../components/Toast.js';
import { TAG_COLOR_PALETTE } from '@captain-tasko/shared';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { RowActionsMenu } from '../components/RowActions.js';
import { useListSearch } from '../lib/use-list-search.js';
import { ListSearchInput } from '../components/ListSearchInput.js';

export function StorageContainersAdmin({ onNavigate }: { onNavigate?: (path: string) => void } = {}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['storage-containers'],
    queryFn: () => apiList<StorageContainer>('/storage-containers'),
  });

  // null = no dialog, undefined value of `editing` = create mode.
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; container: StorageContainer } | null>(null);

  // FR #186 — top-of-list search on name + slug + description.
  // Slug is included because it's the URL-bearable key operators
  // think in terms of when scripting against this list.
  const search = useListSearch(list.data?.data, {
    fields: [
      (c: StorageContainer) => c.name,
      (c) => c.slug,
      (c) => c.description ?? '',
    ],
    urlParam: 'q',
  });

  const { sorted, sort, setSort } = useTableSort(search.filtered, {
    name: (c: StorageContainer) => c.name,
    slug: (c) => c.slug,
    documentCount: (c) => c.documentCount,
    totalBytes: (c) => c.totalBytes,
    createdAt: (c) => c.createdAt,
  }, { key: 'name', dir: 'asc' });

  const del = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/storage-containers/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Container deleted'); qc.invalidateQueries({ queryKey: ['storage-containers'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Storage containers</h1>
          <div className="page-subtitle">{list.data?.data.length ?? 0} active</div>
        </div>
        <button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New container</button>
      </div>

      {list.data && list.data.data.length > 0 && (
        <ListSearchInput
          value={search.query}
          onChange={search.setQuery}
          placeholder="Search containers by name, slug, or description…"
          totalCount={list.data.data.length}
          filteredCount={search.filtered.length}
        />
      )}

      {list.isLoading && <SkeletonRows rows={4} />}
      {list.data && list.data.data.length === 0 && (
        <EmptyState
          title="No containers yet"
          body="Containers help you organize documents into named buckets — like Projects, Reference, Archive."
          action={<button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ Create container</button>}
        />
      )}
      {list.data && list.data.data.length > 0 && search.isActive && search.filtered.length === 0 && (
        <EmptyState
          title="No containers match your search"
          body={`Nothing matches "${search.query}". Try a shorter query or clear the search.`}
          action={<button className="btn" onClick={() => search.setQuery('')}>Clear search</button>}
        />
      )}
      {list.data && list.data.data.length > 0 && search.filtered.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th></th>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
              <SortableTh sortKey="slug" sort={sort} onSortChange={setSort}>Slug</SortableTh>
              <SortableTh sortKey="documentCount" sort={sort} onSortChange={setSort}>Documents</SortableTh>
              <SortableTh sortKey="totalBytes" sort={sort} onSortChange={setSort}>Size</SortableTh>
              <SortableTh sortKey="createdAt" sort={sort} onSortChange={setSort}>Created</SortableTh>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id}>
                <td>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: c.color ?? 'var(--accent-primary)' }} />
                </td>
                <td>
                  {onNavigate ? (
                    <a
                      href={`/storage/${c.id}`}
                      onClick={(e) => { e.preventDefault(); onNavigate(`/storage/${c.id}`); }}
                      style={{ color: 'var(--accent-primary)', fontWeight: 600, textDecoration: 'none' }}
                    >
                      {c.name}
                    </a>
                  ) : (
                    <strong>{c.name}</strong>
                  )}
                  {c.isDefault && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 8, background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>default</span>}
                  {c.description && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.description}</div>}
                </td>
                <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.slug}</code></td>
                <td>{c.documentCount}</td>
                <td>{formatBytes(c.totalBytes)}</td>
                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <RowActionsMenu
                    actions={[
                      // Open the container's file list (#98). Routes
                      // to /storage/:id which renders StorageContainerDetail.
                      ...(onNavigate
                        ? [{ label: 'Open', onClick: () => onNavigate(`/storage/${c.id}`) }]
                        : []),
                      { label: 'Edit…', onClick: () => setDialog({ mode: 'edit', container: c }) },
                      { label: 'separator', separator: true, onClick: () => {} },
                      {
                        label: 'Delete',
                        destructive: true,
                        onClick: () => {
                          // Guard against deleting the default container — it backs new uploads.
                          if (c.isDefault) {
                            toast.error('Cannot delete the default container.');
                            return;
                          }
                          if (c.documentCount > 0) {
                            if (!confirm(`Container "${c.name}" holds ${c.documentCount} document${c.documentCount === 1 ? '' : 's'}. Delete anyway? Documents stay; only the container link is removed.`)) return;
                          } else if (!confirm(`Delete container "${c.name}"?`)) {
                            return;
                          }
                          del.mutate(c.id);
                        },
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dialog && (
        <ContainerDialog
          container={dialog.mode === 'edit' ? dialog.container : null}
          onCancel={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            qc.invalidateQueries({ queryKey: ['storage-containers'] });
          }}
        />
      )}
    </>
  );
}

/**
 * Single dialog handles both create (container=null) and edit modes.
 * In edit mode, slug becomes editable — it's normally derived from name on
 * create, but operators may want to rename buckets without losing the path.
 */
function ContainerDialog({
  container,
  onCancel,
  onSaved,
}: {
  container: StorageContainer | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = container !== null;
  const [name, setName] = useState(container?.name ?? '');
  const [slug, setSlug] = useState(container?.slug ?? '');
  const [description, setDescription] = useState(container?.description ?? '');
  const [color, setColor] = useState<string>(container?.color ?? TAG_COLOR_PALETTE[0]!);

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return apiSingle<StorageContainer>(`/storage-containers/${container.id}`, {
          method: 'PATCH',
          body: {
            name,
            slug: slug || undefined,
            description: description || null,
            color,
          },
        });
      }
      return apiSingle<StorageContainer>('/storage-containers', {
        method: 'POST',
        body: { name, description: description || undefined, color },
      });
    },
    onSuccess: () => { toast.success(isEdit ? 'Container updated' : 'Container created'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      role="dialog"
      aria-labelledby="container-dlg-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }}
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 480 }}>
        <h2 id="container-dlg-title" style={{ margin: 0, marginBottom: 16 }}>
          {isEdit ? `Edit container: ${container.name}` : 'New storage container'}
        </h2>

        <div className="field">
          <label htmlFor="c-name">Name</label>
          <input id="c-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Reference, Archive, Projects/Acme" />
        </div>

        {isEdit && (
          <div className="field">
            <label htmlFor="c-slug">Slug</label>
            <input
              id="c-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="reference"
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Lowercase letters, digits, and hyphens. Backs the storage path; changing it doesn't move existing files.
            </div>
          </div>
        )}

        <div className="field">
          <label htmlFor="c-desc">Description</label>
          <input id="c-desc" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="field">
          <label>Color</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {TAG_COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setColor(c)}
                style={{ width: 22, height: 22, borderRadius: 4, border: `2px solid ${c === color ? 'var(--text-primary)' : 'transparent'}`, background: c, cursor: 'pointer', padding: 0 }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Create container')}
          </button>
        </div>
      </div>
    </div>
  );
}
